/**
 * modeDuDocument — LA lecture du mode d'édition SuperDoc, et la seule.
 *
 * ## Pourquoi ce module existe (autopsie du 2026-08-27, conv CD3 `76a7101d`)
 *
 * L'écriture d'un acte a été refusée par le fournisseur avec
 * `PERMISSION_DENIED: insert is unavailable while the document is in read-only
 * review mode.` Or **aucune surface ne publiait le mode du document** :
 * `canvas_inspect` rendait `revision`, `counts`, `blocks`, `executableActions`…
 * et rien sur `editing` / `suggesting` / `viewing`. L'agent ne découvrait la
 * lecture seule qu'en s'y cognant — donc trop tard pour la dire à l'avocat — et
 * la trace backend ne permettait pas de l'établir après coup.
 *
 * Le mode est désormais publié dans le receipt d'inspection
 * (`superDocCanvasExecutor.executeInspect`). Il est lu ICI, en un seul endroit :
 * deux lecteurs du même champ divergeraient au premier changement de surface du
 * fournisseur.
 *
 * ## `'unknown'` n'est pas `'editing'`
 *
 * `config.documentMode` peut être absent (instance non montée, hôte de test,
 * surface renommée par une version de SuperDoc). Ce cas rend `'unknown'`, JAMAIS
 * `'editing'` : replier une lecture ratée sur « le document est inscriptible »
 * ferait affirmer au modèle une capacité qu'on n'a pas constatée. C'est la
 * doctrine constante du module canvas — un signal absent dit « je ne sais pas »,
 * pas « ça vaut zéro ».
 */
import type { CanvasDocumentMode } from '@hector/shared-types';
import type { SuperDocInstance } from '@superdoc/react';

/**
 * Les trois modes que SuperDoc sait RECEVOIR (`setDocumentMode`).
 *
 * `'unknown'` n'en fait volontairement pas partie : c'est un constat de lecture,
 * pas un mode que l'on peut poser sur le document.
 */
export type ModeDocumentEcrivable = Exclude<CanvasDocumentMode, 'unknown'>;

/**
 * Forme minimale de l'instance SuperDoc utilisée ici.
 *
 * `documentMode` est typé `unknown` et non `string` : le fournisseur ne garantit
 * pas son vocabulaire, et le seul contrat qui tienne est celui de la garde
 * ci-dessous.
 */
interface InstanceAvecModeDeDocument {
  config?: { documentMode?: unknown };
  setDocumentMode?: (mode: ModeDocumentEcrivable) => void;
}

/** Vue étroite de l'instance, sans `any` — la surface n'est pas typée par `@superdoc/react`. */
export function vueModeDuDocument(instance: SuperDocInstance | null): InstanceAvecModeDeDocument | null {
  return instance === null ? null : (instance as unknown as InstanceAvecModeDeDocument);
}

/**
 * Le mode réellement monté, ou `'unknown'` si on n'a pas su le lire.
 *
 * Ne rend jamais `null` ni `undefined` : l'appelant qui publie ce mode au modèle
 * doit toujours avoir quelque chose à dire, et l'aveu d'ignorance en fait partie.
 */
export function lireLeModeDuDocument(instance: SuperDocInstance | null): CanvasDocumentMode {
  const lu = vueModeDuDocument(instance)?.config?.documentMode;
  return lu === 'editing' || lu === 'suggesting' || lu === 'viewing' ? lu : 'unknown';
}

/**
 * Le mode que l'éditeur porte sous un régime de suivi donné — LA traduction, et
 * la seule : activé → `suggesting`, coupé → `editing`.
 *
 * Le régime (`reviewEnabled` du scope documentaire) est ce que le bouton de la
 * barre d'outils affiche ; le mode est ce que l'éditeur applique aux frappes.
 * Deux traductions divergeraient, et le bouton afficherait un suivi que les
 * frappes n'ont pas (#1838).
 */
export function modeDuRegime(suiviActif: boolean): ModeDocumentEcrivable {
  return suiviActif ? 'suggesting' : 'editing';
}

/**
 * Pose sur l'éditeur le mode que commande le régime de suivi, puis rend le mode
 * RELU — jamais le mode demandé.
 *
 * ⚠️ `setDocumentMode` est appelé COMME MÉTHODE de l'instance, jamais extrait
 * dans une variable : SuperDoc y lit des champs privés de classe, et un appel
 * détaché perd son `this` — exception au runtime, canvas emporté par
 * l'`AppErrorBoundary` (constaté le 2026-06-09).
 */
export function alignerLeModeSurLeRegime(instance: SuperDocInstance | null, suiviActif: boolean): CanvasDocumentMode {
  const vue = vueModeDuDocument(instance);
  const voulu = modeDuRegime(suiviActif);
  if (vue !== null && typeof vue.setDocumentMode === 'function' && lireLeModeDuDocument(instance) !== voulu) {
    vue.setDocumentMode(voulu);
  }
  return lireLeModeDuDocument(instance);
}
